import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatTableModule } from '@angular/material/table';

import { BasketballScoringAdapter } from '@core/sports/basketball/basketball-scoring.adapter';
import { BoxScoreLine, PLAY_KIND_LABEL } from '@core/sports/basketball/basketball.models';
import { BrijjSportsStateService } from '@core/state/brijjsports-state.service';

@Component({
  selector: 'app-basketball-spectator',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTableModule],
  templateUrl: './basketball-spectator.component.html',
  styleUrl: './basketball-spectator.component.scss'
})
export class BasketballSpectatorComponent {
  readonly sessionId = input.required<string>();

  protected readonly state = inject(BrijjSportsStateService);
  protected readonly adapter = inject(BasketballScoringAdapter);
  protected readonly boxColumns = ['name', 'pts', 'fg', '3p', 'ft', 'reb', 'ast', 'stl', 'blk'];

  protected readonly session = computed(() => this.state.sessionById(this.sessionId()));
  protected readonly game = computed(() => this.state.basketballGameBySession(this.sessionId()));
  protected readonly clock = computed(() => this.game()?.clock ?? null);
  protected readonly homeScore = computed(() => {
    const g = this.game();
    return g ? this.adapter.teamScore(g, 'home') : 0;
  });
  protected readonly awayScore = computed(() => {
    const g = this.game();
    return g ? this.adapter.teamScore(g, 'away') : 0;
  });
  protected readonly clockDisplay = computed(() => formatClock(this.clock()?.remainingSec ?? 0));

  protected readonly recentPlays = computed(() => {
    const plays = this.game()?.plays ?? [];
    return [...plays]
      .reverse()
      .slice(0, 12)
      .map(p => ({
        id: p.id,
        team: p.team,
        quarter: p.quarter,
        clockSec: p.clockSec,
        playerName: this.state.athleteById(p.athleteId)?.name ?? (p.team === 'away' ? 'Away team' : '—'),
        label: PLAY_KIND_LABEL[p.kind]
      }));
  });

  protected readonly boxRows = computed<(BoxScoreLine & { name: string })[]>(() => {
    const g = this.game();
    if (!g) return [];
    return this.adapter.boxScore(g).map(row => ({
      ...row,
      name: this.state.athleteById(row.athleteId)?.name ?? '—'
    }));
  });

  formatClockSec(sec: number): string {
    return formatClock(sec);
  }
  trackPlay(_: number, p: { id: string }): string {
    return p.id;
  }
  trackBox(_: number, r: { athleteId: string }): string {
    return r.athleteId;
  }
}

function formatClock(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
